import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import type {
    AccessData,
    Customer,
    CustomerLoyaltyProgram,
    InventoryItem,
    InventoryItemLocation,
    InventoryItemTransaction,
    LoyaltyProgramData,
    PermissionData,
    Setting,
    TimesheetRecord,
} from "./types"
import type { Assignment, LocationDetail, ServiceDetail } from "../../graphql/types"
import { SettingsUpdateType } from "../screens/settings/types"

export const DATA_LOADING_ERROR = "DATA_LOADING_ERROR"
export const createActionLoadingError = (error: any) => ({
    type: DATA_LOADING_ERROR,
    payload: {
        error: error,
    },
})

export const ACCOUNT_IMAGE_DELETED = "data/accountImage/deleted"
export const accountImageDeleted = (id: string) => ({
    type: ACCOUNT_IMAGE_DELETED,
    payload: {
        id: id,
    },
})

export const INVENTORY_ITEM_CREATED = "data/inventoryItem/created"
export const inventoryItemCreated = (inventoryItem: InventoryItem) => ({
    type: INVENTORY_ITEM_CREATED,
    payload: {
        inventoryItem: inventoryItem,
    },
})

export const INVENTORY_ITEM_UPDATED = "data/inventoryItem/updated"
export const inventoryItemUpdated = (newValue: InventoryItem) => ({
    type: INVENTORY_ITEM_UPDATED,
    payload: {
        newValue: newValue,
    },
})

export const INVENTORY_ITEM_DELETED = "data/inventoryItem/deleted"
export const inventoryItemDeleted = (id: string) => ({
    type: INVENTORY_ITEM_DELETED,
    payload: {
        id: id,
    },
})

export const INVENTORY_ITEM_LOCATION_CREATED = "data/inventoryItemLocation/created"
export const inventoryItemLocationCreated = (inventoryItemLocation: InventoryItemLocation) => ({
    type: INVENTORY_ITEM_LOCATION_CREATED,
    payload: {
        inventoryItemLocation: inventoryItemLocation,
    },
})

export const INVENTORY_ITEM_LOCATION_UPDATED = "data/inventoryItemLocation/updated"
export const inventoryItemLocationUpdated = (newValue: InventoryItemLocation) => ({
    type: INVENTORY_ITEM_LOCATION_UPDATED,
    payload: {
        newValue: newValue,
    },
})

export const INVENTORY_ITEM_LOCATION_DELETED = "data/inventoryItemLocation/deleted"
export const inventoryItemLocationDeleted = (inventoryItemLocationId: string, inventoryItemId: string) => ({
    type: INVENTORY_ITEM_LOCATION_DELETED,
    payload: {
        inventoryItemLocationId: inventoryItemLocationId,
        inventoryItemId: inventoryItemId,
    },
})

export const INVENTORY_ITEM_TRANSACTION_CREATED = "data/inventoryItemTransaction/created"
export const inventoryItemTransactionCreated = (inventoryItemTransaction: InventoryItemTransaction) => ({
    type: INVENTORY_ITEM_TRANSACTION_CREATED,
    payload: {
        inventoryItemTransaction: inventoryItemTransaction,
    },
})
// Customer Loyalty Program

export const CUSTOMER_LOYALTY_PROGRAM_CREATED = "data/customerLoyaltyProgram/created"
export const customerLoyaltyProgramCreated = (customerLoyaltyProgram: CustomerLoyaltyProgram) => ({
    type: CUSTOMER_LOYALTY_PROGRAM_CREATED,
    payload: {
        customerLoyaltyProgram: customerLoyaltyProgram,
    },
})

export const CUSTOMER_LOYALTY_PROGRAM_UPDATED = "data/customerLoyaltyProgram/updated"
export const customerLoyaltyProgramUpdated = (newValue: Partial<CustomerLoyaltyProgram>) => ({
    type: CUSTOMER_LOYALTY_PROGRAM_UPDATED,
    payload: {
        newValue: newValue,
    },
})

export const LOYALTY_PROGRAM_CREATED = "data/loyaltyProgram/created"
export const loyaltyProgramCreated = (loyaltyProgram: LoyaltyProgramData) => ({
    type: LOYALTY_PROGRAM_CREATED,
    payload: {
        loyaltyProgram: loyaltyProgram,
    },
})

export const LOYALTY_PROGRAM_UPDATED = "data/loyaltyProgram/updated"
export const loyaltyProgramUpdated = (newValue: LoyaltyProgramData) => ({
    type: LOYALTY_PROGRAM_UPDATED,
    payload: {
        newValue: newValue,
    },
})

export const LOYALTY_PROGRAM_DELETED = "data/loyaltyProgram/deleted"
export const loyaltyProgramDeleted = (id: string) => ({
    type: LOYALTY_PROGRAM_DELETED,
    payload: {
        id: id,
    },
})

export const SETTING_CREATED = "data/setting/created"
export const settingCreated = (setting: Setting) => ({
    type: SETTING_CREATED,
    payload: {
        setting: setting,
    },
})

export const SETTING_UPDATED = "data/setting/updated"
export const settingUpdated = (newValue: Setting) => ({
    type: SETTING_UPDATED,
    payload: {
        newValue: newValue,
    },
})

export const SETTINGS_UPDATED = "SETTINGS_UPDATED"
export const settingsUpdate = (settings: SettingsUpdateType[]) => ({
    type: SETTINGS_UPDATED,
    payload: {
        settings: settings,
    },
})

export const SETTING_DELETED = "data/setting/deleted"
export const settingDeleted = (settingId: string) => ({
    type: SETTING_DELETED,
    payload: {
        settingId: settingId,
    },
})

export const ACCESS_LEVEL_CREATED = "data/accessLevel/created"
export const accessLevelCreated = (accessLevel: AccessData, useEventFlow: boolean = false) => ({
    type: ACCESS_LEVEL_CREATED,
    payload: {
        accessLevel: accessLevel,
        useEventFlow: useEventFlow,
    },
})

export const ACCESS_LEVEL_UPDATED = "data/accessLevel/updated"
export const accessLevelUpdated = (newValue: AccessData, useEventFlow: boolean = false) => ({
    type: ACCESS_LEVEL_UPDATED,
    payload: {
        newValue: newValue,
        useEventFlow: useEventFlow,
    },
})

export const ACCESS_LEVEL_DELETED = "data/accessLevel/deleted"
export const accessLevelDeleted = (accessId: string, useEventFlow: boolean = false) => ({
    type: ACCESS_LEVEL_DELETED,
    payload: {
        accessId: accessId,
        useEventFlow: useEventFlow,
    },
})

export const PERMISSION_CREATED = "data/permission/created"
export const permissionCreated = (permission: PermissionData, useEventFlow: boolean = false) => ({
    type: PERMISSION_CREATED,
    payload: {
        permission: permission,
        useEventFlow: useEventFlow,
    },
})

export const PERMISSION_DELETED = "data/permission/deleted"
export const permissionDeleted = (permissionId: string, accessId: string, useEventFlow: boolean = false) => ({
    type: PERMISSION_DELETED,
    payload: {
        permissionId: permissionId,
        accessId: accessId,
        useEventFlow: useEventFlow,
    },
})

export const TIME_RECORD_CREATED = "data/time-record/created"
export const timeRecordCreated = (timeRecord: Partial<TimesheetRecord>) => ({
    type: TIME_RECORD_CREATED,
    payload: {
        timeRecord: timeRecord,
    },
})

export const TIME_RECORD_UPDATED = "data/time-record/updated"
export const timeRecordUpdated = (newValue: Partial<TimesheetRecord>, oldValue: Maybe<TimesheetRecord>) => ({
    type: TIME_RECORD_UPDATED,
    payload: {
        newValue: newValue,
        oldValue: oldValue,
    },
})

export const TIME_RECORD_DELETED = "data/time-record/deleted"
export const timeRecordDeleted = (id: string, userAccountId: string) => ({
    type: TIME_RECORD_DELETED,
    payload: {
        id: id,
        userAccountId: userAccountId,
    },
})

export const CUSTOMER_UPDATED = "data/customer/updated"
export const customerUpdated = (newValue: Customer, oldValue: Maybe<Customer>, currentPage: number) => ({
    type: CUSTOMER_UPDATED,
    payload: {
        currentPage: currentPage,
        newValue: newValue,
        oldValue: oldValue,
    },
})

export const CUSTOMER_DELETED = "data/customer/deleted"
export const customerDeleted = (customer: Customer, currentPage: number) => ({
    type: CUSTOMER_DELETED,
    payload: {
        customer: customer,
        currentPage: currentPage,
    },
})

export const LOCATION_CREATED = "data/location/created"
export const locationCreated = (location: LocationDetail) => ({
    type: LOCATION_CREATED,
    payload: {
        location: location,
    },
})

export const LOCATION_DELETED = "data/location/deleted"
export const locationDeleted = (locationId: string) => ({
    type: LOCATION_DELETED,
    payload: {
        locationId: locationId,
    },
})

export const LOCATION_UPDATED = "data/location/updated"
export const locationUpdated = (newValue: Partial<LocationDetail>) => ({
    type: LOCATION_UPDATED,
    payload: {
        newValue: newValue,
    },
})

export const TERMINAL_CREATED = "data/terminal/created"
export const terminalCreated = (terminal: ServiceDetail) => ({
    type: TERMINAL_CREATED,
    payload: {
        terminal: terminal,
    },
})

export const TERMINAL_UPDATED = "data/terminal/updated"
export const terminalUpdated = (
    newValue: Partial<ServiceDetail>,
    showNotification?: boolean,
    skipRequest?: boolean
) => ({
    type: TERMINAL_UPDATED,
    payload: {
        newValue: newValue,
        showNotification: showNotification,
        skipRequest: skipRequest,
    },
})

export const ASSIGNMENT_CREATED = "data/assignment/created"
export const assignmentCreated = (assignment: Assignment) => ({
    type: ASSIGNMENT_CREATED,
    payload: {
        assignment: assignment,
    },
})

export const ASSIGNMENT_DELETED = "data/assignment/deleted"
export const assignmentDeleted = (assignmentId: string, locationId: Maybe<string>) => ({
    type: ASSIGNMENT_DELETED,
    payload: {
        assignmentId: assignmentId,
        locationId: locationId,
    },
})

export const ASSIGNMENT_UPDATED = "data/assignment/updated"
export const assignmentUpdated = (newValue: Partial<Assignment>) => ({
    type: ASSIGNMENT_UPDATED,
    payload: {
        newValue: newValue,
    },
})

export const ASSIGN_OPTION_GROUP_TO_PRODUCT = "data/assign-option-group/assign"
export const assignOptionGroupToProduct = (optionGroupId: Maybe<string>) => ({
    type: ASSIGN_OPTION_GROUP_TO_PRODUCT,
    payload: {
        optionGroupId: optionGroupId,
    },
})

export const ASSIGN_MODIFIER_GROUP_TO_PRODUCT = "data/assign-modifier-group/assign"
export const assignModifierGroupToProduct = (modifierGroupId: Maybe<string>) => ({
    type: ASSIGN_MODIFIER_GROUP_TO_PRODUCT,
    payload: {
        modifierGroupId: modifierGroupId,
    },
})
